/**
 * Tiered bundle pricing for stickers.
 *
 * Unit price: $4.99
 * Pack of 5:  $22.99
 * Pack of 10: $39.99
 *
 * Algorithm: maximise pack-10s first, then pack-5s, remainder at unit price.
 *
 * Examples:
 *   1  → 1 × 4.99 = 4.99
 *   5  → 1 × pack5 = 22.99
 *   7  → 1 × pack5 + 2 × unit = 22.99 + 9.98 = 32.97
 *  10  → 1 × pack10 = 39.99
 *  14  → 1 × pack10 + 4 × unit = 39.99 + 19.96 = 59.95
 *  15  → 1 × pack10 + 1 × pack5 = 62.98
 *  20  → 2 × pack10 = 79.98
 *  23  → 2 × pack10 + 3 × unit = 79.98 + 14.97 = 94.95
 *  25  → 2 × pack10 + 1 × pack5 = 79.98 + 22.99 = 102.97
 */

export const UNIT_PRICE = 4.99;
export const PACK_5_PRICE = 22.99;
export const PACK_10_PRICE = 39.99;

export interface BundleLine {
  label: string;
  qty: number;
  unitLabel: string;
  lineTotal: number;
}

export interface BundleBreakdown {
  lines: BundleLine[];
  totalBundlePrice: number;
  totalFullPrice: number;
  totalDiscount: number;
}

/**
 * Calculate the optimal bundle breakdown for a given sticker count.
 * Returns null when count <= 0.
 */
/**
 * Maximum number of stickers eligible for the pack discount on Shopify side.
 * Discount codes only exist up to EF-PACK-50, so any sticker beyond the
 * 50th is billed at unit price by Shopify. The UI MUST mirror that exactly
 * to avoid a mismatch between displayed total and the amount actually charged.
 */
export const BUNDLE_TIER_CAP = 50;

export function calculateBundle(count: number): BundleBreakdown | null {
  if (count <= 0) return null;

  // Stickers above the cap are billed at unit price (no pack discount on Shopify).
  const bundled = Math.min(count, BUNDLE_TIER_CAP);
  const overflow = count - bundled;

  const pack10s = Math.floor(bundled / 10);
  const remainderAfter10 = bundled % 10;
  const pack5s = Math.floor(remainderAfter10 / 5);
  const singles = (remainderAfter10 % 5) + overflow;

  const lines: BundleLine[] = [];

  if (pack10s > 0) {
    lines.push({
      label: `Pack 10`,
      qty: pack10s,
      unitLabel: `${PACK_10_PRICE.toFixed(2)}$`,
      lineTotal: pack10s * PACK_10_PRICE,
    });
  }

  if (pack5s > 0) {
    lines.push({
      label: `Pack 5`,
      qty: pack5s,
      unitLabel: `${PACK_5_PRICE.toFixed(2)}$`,
      lineTotal: pack5s * PACK_5_PRICE,
    });
  }

  if (singles > 0) {
    lines.push({
      label: `Unitaire`,
      qty: singles,
      unitLabel: `${UNIT_PRICE.toFixed(2)}$`,
      lineTotal: singles * UNIT_PRICE,
    });
  }

  const totalBundlePrice = parseFloat(
    lines.reduce((sum, l) => sum + l.lineTotal, 0).toFixed(2)
  );
  const totalFullPrice = parseFloat((count * UNIT_PRICE).toFixed(2));
  const totalDiscount = parseFloat(
    Math.max(0, totalFullPrice - totalBundlePrice).toFixed(2)
  );

  return { lines, totalBundlePrice, totalFullPrice, totalDiscount };
}

/**
 * Returns the Shopify discount code matching the bundled sticker count.
 * Codes exist for every multiple of 5 from 5 to 50 (EF-PACK-5 ... EF-PACK-50).
 * Bundle pricing is bracketed by the highest multiple of 5 <= count, capped at 50.
 * Returns null when no bundle discount applies (count < 5).
 */
export function getBundleDiscountCode(count: number): string | null {
  if (count < 5) return null;
  const tier = Math.min(50, Math.floor(count / 5) * 5);
  return `EF-PACK-${tier}`;
}

/**
 * Hints shown when user is close to a better bundle tier.
 */
export function getPromoHints(count: number): Array<{ text: string; saved: string }> {
  if (count <= 0) return [];
  const hints: Array<{ text: string; saved: string }> = [];

  const remainderAfter10 = count % 10;
  const remainderAfter5 = count % 5;

  // Close to a pack of 5 (need 1-2 more)
  if (remainderAfter5 >= 3) {
    const needed = 5 - remainderAfter5;
    if (needed <= 2 && needed > 0) {
      const wouldPay = remainderAfter5 * UNIT_PRICE;
      const packCost = PACK_5_PRICE;
      // Extra cost to reach pack vs savings
      const extraCost = needed * UNIT_PRICE;
      const savings = parseFloat((wouldPay + extraCost - packCost - extraCost + (wouldPay - packCost)).toFixed(2));
      // Simpler: savings = full price of 5 at unit - pack5 price
      const realSavings = parseFloat((5 * UNIT_PRICE - PACK_5_PRICE).toFixed(2));
      if (realSavings > 0) {
        hints.push({
          text: `Plus que ${needed} pour un Pack 5 !`,
          saved: `${realSavings.toFixed(2)}$`,
        });
      }
    }
  }

  // Close to a pack of 10 (need 1-3 more)
  if (remainderAfter10 >= 7) {
    const needed = 10 - remainderAfter10;
    const realSavings = parseFloat((10 * UNIT_PRICE - PACK_10_PRICE).toFixed(2));
    if (realSavings > 0) {
      hints.push({
        text: `Plus que ${needed} pour un Pack 10 !`,
        saved: `${realSavings.toFixed(2)}$`,
      });
    }
  }

  return hints;
}
